import { cardPayOnlineLabel, parseMoneyToCents, qrPayLabel, tt } from '@agos/shared'

/**
 * *Prehľad predaja* - what one person sold, by category and by item - `FRONTEND.md` §9.666, owner O45 and O50.
 *
 * **The owner**: *"Sumáre -> when clicking on someone u see popup -> we have Objednávky / Stoly and add another thing
 * «Prehľad Predaja» ... Show there: Kategória, Počet obj in that kategória, tržba, cardpay, hotovosť, karta terminál,
 * karta kuriér ... And when clicking on tat kategória make it collapsable and show another things: What item was
 * selled"* - and then *"add also that there will be checkbox and he can select each item (or whole category) and it
 * will calculate it at the bottom to him"*.
 *
 * **They answered both of the questions this was banked with**, which is what let it be built:
 * - *"lines (for example 3x kofola + 3 kofola is 6 kofola)"* - the quantity is **lines summed**, so two lines of
 *   three on one order are six and the same product on two orders adds up. §10A.336's query is `SUM(quantity)`.
 * - *"qr_pay should be also there"* - **five** payment columns rather than four.
 *
 * **The figures come from the server and are not recomputed here.** §10A.336 groups them in SQL against
 * `order_items`, which is where a category and an item name live - the popup's own `orders` list has neither. What
 * this file does is the **selection**, which is a screen's business: which rows are ticked and what they come to.
 */

/**
 * One payment column, in the order the owner listed them - with *QR Pay* moved beside *CardPay online*, which is
 * where the owner asked for it on every screen that counts the online card (§9.767, owner O129).
 */
export const SALES_MONEY_COLUMNS = [
  'cardOnline',
  'qrPay',
  'cash',
  'cardTerminal',
  'cardCourier',
] as const

export type SalesMoneyColumn = (typeof SALES_MONEY_COLUMNS)[number]

/** What each column is called on screen - the owner's own words for four of the five. */
export function salesColumnLabel(column: SalesMoneyColumn): string {
  switch (column) {
    case 'cardOnline':
      // §9.767 - the online card's one name; this column said *CardPay*.
      return cardPayOnlineLabel()
    case 'cash':
      return tt('Hotovosť')
    case 'cardTerminal':
      return tt('Karta terminál')
    case 'cardCourier':
      return tt('Karta kuriér')
    case 'qrPay':
      // BACKEND.md §10A.390 (§9.741): a paid door QR payment is filed `qr_pay`, by card or by transfer on the
      // bank's page alike, so this column is the person's door QR takings. Until then nothing wrote the value.
      // *QR Pay*, the owner's own word for it, as on every other screen - it read *QR platba* two tabs from a
      // *QR Pay* tile.
      return qrPayLabel()
  }
}

export interface SalesItemRow {
  item: string
  quantity: number
  orders: number
  revenue: number
  cardOnline: number
  cash: number
  cardTerminal: number
  cardCourier: number
  qrPay: number
}

/**
 * What a row of the breakdown is - `FRONTEND.md` §9.695, `BACKEND.md` §10A.352, owner #14 / O67.
 *
 * **The goods, then four rows that close the sum.** The owner: *"keď je zľava na danú obj tak mu ju neukazuje pri
 * danom produkte ... nesedia peniaze"*. The figures are now net of the discount, and what is not a product - the
 * delivery fee, the deposits, the five-cent rounding and a stored total that disagrees with its own lines - is a row
 * of its own, so every row together is the popup's *Celkovo*.
 */
export const SALES_ROW_KINDS = ['goods', 'delivery', 'deposits', 'rounding', 'difference'] as const

export type SalesRowKind = (typeof SALES_ROW_KINDS)[number]

export interface SalesCategoryRow extends Omit<SalesItemRow, 'item'> {
  category: string
  /** `goods` for a category of products; the other four close the sum - §10A.352. */
  kind: SalesRowKind
  items: SalesItemRow[]
}

/**
 * What a row is called on screen.
 *
 * **A category of goods keeps the name the franchise gave it**; the four closing rows are labelled here, through the
 * translator, rather than by printing the server's Slovak - the server still sends a name, which is what an older
 * screen would draw.
 */
export function salesRowLabel(row: Pick<SalesCategoryRow, 'category' | 'kind'>): string {
  switch (row.kind) {
    case 'goods':
      return row.category
    case 'delivery':
      return tt('Doprava')
    case 'deposits':
      return tt('Zálohy')
    case 'rounding':
      return tt('Zaokrúhlenie')
    case 'difference':
      return tt('Rozdiel oproti položkám')
  }
}

/** A row's kind off the payload. Absent - a server older than §10A.352 - is a category of goods, which it was. */
function kindOf(value: unknown): SalesRowKind {
  return SALES_ROW_KINDS.find((kind) => kind === value) ?? 'goods'
}

/**
 * The breakdown as the screen draws it, from whatever the payload answered.
 *
 * **Figures are read as money in cents and kept as cents from here on**, because the server answers euro as a
 * `double` and adding five of those on a screen is how a total comes out at 2 971,399999. §14.7's rule.
 */
export function salesBreakdownOf(payload: unknown): SalesCategoryRow[] {
  if (!Array.isArray(payload)) return []
  const rows: SalesCategoryRow[] = []
  for (const raw of payload) {
    if (typeof raw !== 'object' || raw === null) continue
    const row = raw as Record<string, unknown>
    const category = typeof row.category === 'string' && row.category.trim() !== '' ? row.category.trim() : null
    if (category === null) continue
    rows.push({
      category,
      kind: kindOf(row.kind),
      ...figuresOf(row),
      items: Array.isArray(row.items)
        ? row.items.flatMap((entry) => {
            if (typeof entry !== 'object' || entry === null) return []
            const item = entry as Record<string, unknown>
            const name = typeof item.item === 'string' && item.item.trim() !== '' ? item.item.trim() : null
            if (name === null) return []
            return [{ item: name, ...figuresOf(item) }]
          })
        : [],
    })
  }
  return rows
}

function figuresOf(row: Record<string, unknown>): Omit<SalesItemRow, 'item'> {
  return {
    quantity: typeof row.quantity === 'number' ? row.quantity : 0,
    orders: typeof row.orders === 'number' ? row.orders : 0,
    revenue: cents(row.revenue),
    cardOnline: cents(row.cardOnline),
    cash: cents(row.cash),
    cardTerminal: cents(row.cardTerminal),
    cardCourier: cents(row.cardCourier),
    qrPay: cents(row.qrPay),
  }
}

function cents(value: unknown): number {
  return typeof value === 'number' || typeof value === 'string' ? parseMoneyToCents(value) : 0
}

/** The key a ticked row is remembered by. A category and an item are different things with the same name sometimes. */
export function salesItemKey(category: string, item: string): string {
  return `${category}\u0000${item}`
}

/**
 * What the ticked rows come to - owner O50's *"it will calculate it at the bottom to him"*.
 *
 * **A ticked category means its items**, not the category's own figures: ticking *Pizza* and then unticking one pizza
 * inside it has to leave the rest, and a category total added beside its own items would double everything. So the
 * selection is only ever over items, and a category tick is a shorthand for all of them - which is also why
 * `categoryState` below has three answers rather than two.
 *
 * **Nothing ticked is nought rather than everything**, because a total that starts as the whole table reads as a
 * figure somebody chose.
 */
export function selectedTotals(
  categories: readonly SalesCategoryRow[],
  selected: ReadonlySet<string>,
): Omit<SalesItemRow, 'item'> & { items: number } {
  const total = {
    items: 0,
    quantity: 0,
    orders: 0,
    revenue: 0,
    cardOnline: 0,
    cash: 0,
    cardTerminal: 0,
    cardCourier: 0,
    qrPay: 0,
  }
  for (const category of categories) {
    for (const item of category.items) {
      if (!selected.has(salesItemKey(category.category, item.item))) continue
      total.items += 1
      total.quantity += item.quantity
      /* **Summed, and that is stated rather than hidden**: one order holding two ticked items counts in both, so this
         is *how many order-lines' worth* rather than a count of distinct orders. The owner asked for *počet
         objednávok* per item, which is what each row shows; a distinct count across a selection would need the order
         ids, which this read does not carry. */
      total.orders += item.orders
      total.revenue += item.revenue
      total.cardOnline += item.cardOnline
      total.cash += item.cash
      total.cardTerminal += item.cardTerminal
      total.cardCourier += item.cardCourier
      total.qrPay += item.qrPay
    }
  }
  return total
}

/**
 * Whether a category's tick is on, off or partly on.
 *
 * Three answers because a category is a shorthand for its items: with some of them ticked, neither *on* nor *off* is
 * true, and a checkbox that showed *off* there would un-tick the rest the moment somebody pressed it.
 */
export function categoryState(
  category: SalesCategoryRow,
  selected: ReadonlySet<string>,
): 'all' | 'some' | 'none' {
  if (category.items.length === 0) return 'none'
  const ticked = category.items.filter((item) =>
    selected.has(salesItemKey(category.category, item.item)),
  ).length
  if (ticked === 0) return 'none'
  return ticked === category.items.length ? 'all' : 'some'
}

/** Ticking or unticking a whole category - every item in it, so the total and the box agree. */
export function toggleCategory(
  category: SalesCategoryRow,
  selected: ReadonlySet<string>,
): Set<string> {
  const next = new Set(selected)
  const turningOn = categoryState(category, selected) !== 'all'
  for (const item of category.items) {
    const key = salesItemKey(category.category, item.item)
    if (turningOn) next.add(key)
    else next.delete(key)
  }
  return next
}

/** Ticking or unticking one item. */
export function toggleItem(
  category: string,
  item: string,
  selected: ReadonlySet<string>,
): Set<string> {
  const next = new Set(selected)
  const key = salesItemKey(category, item)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  return next
}

/**
 * Every row together - the *Spolu* line under the table, `FRONTEND.md` §9.695, owner #14 / O67.
 *
 * **The goods and the closing rows alike**, because together they are the money: §10A.352 makes the rows add up to the
 * popup's *Celkovo* and each payment column to that method's own card, so this line is the owner's own check written
 * down. In cents, like everything parsed here.
 */
export function salesTotals(
  categories: readonly SalesCategoryRow[],
): Pick<SalesCategoryRow, 'revenue' | SalesMoneyColumn> {
  const total = { revenue: 0, cardOnline: 0, cash: 0, cardTerminal: 0, cardCourier: 0, qrPay: 0 }
  for (const category of categories) {
    total.revenue += category.revenue
    for (const column of SALES_MONEY_COLUMNS) total[column] += category[column]
  }
  return total
}

/**
 * How far the rows are from *Celkovo*, in cents - nought when they agree.
 *
 * §10A.352 makes this nought by construction, so a non-zero answer means a server older than it, whose rows are the
 * gross figures. **Said rather than left to be noticed**, because two figures on one screen disagreeing in silence
 * is the owner's complaint itself.
 */
export function salesGapCents(categories: readonly SalesCategoryRow[], celkovoCents: number): number {
  if (categories.length === 0) return 0
  return celkovoCents - salesTotals(categories).revenue
}
